import { ApiService } from "../../abstractions/api.service";
import { AppIdService } from "../../abstractions/appId.service";
import { AuthService } from "../../abstractions/auth.service";
import { CryptoService } from "../../abstractions/crypto.service";
import { LogService } from "../../abstractions/log.service";
import { MessagingService } from "../../abstractions/messaging.service";
import { PlatformUtilsService } from "../../abstractions/platformUtils.service";
import { StateService } from "../../abstractions/state.service";
import { TokenService } from "../../abstractions/token.service";
import { TwoFactorService } from "../../abstractions/twoFactor.service";
import { AuthResult } from "../../models/domain/auth-result";
import { GoogleLogInCredentials } from "../../models/domain/log-in-credentials";
import { GoogleTokenRequest } from "../../models/request/identity-token/google-token.request";

import { LogInStrategy } from "./logIn.strategy";

/**
 * Deliberately thin, and deliberately independent of SsoLogInStrategy /
 * KeyConnectorService — Google auth only proves identity, it never carries or
 * unwraps a vault key. `processTokenResponse` (inherited unchanged) already
 * handles `key == null` by surfacing `resetMasterPassword`, so a brand-new
 * Google user is routed to set a master password exactly like today's normal
 * registration completion. See GOOGLE_SSO_PLAN.md.
 */
export class GoogleLogInStrategy extends LogInStrategy {
  tokenRequest: GoogleTokenRequest;

  constructor(
    cryptoService: CryptoService,
    apiService: ApiService,
    tokenService: TokenService,
    appIdService: AppIdService,
    platformUtilsService: PlatformUtilsService,
    messagingService: MessagingService,
    logService: LogService,
    stateService: StateService,
    twoFactorService: TwoFactorService,
    private authService: AuthService,
  ) {
    super(
      cryptoService,
      apiService,
      tokenService,
      appIdService,
      platformUtilsService,
      messagingService,
      logService,
      stateService,
      twoFactorService,
    );
  }

  async logIn(credentials: GoogleLogInCredentials): Promise<AuthResult> {
    this.tokenRequest = new GoogleTokenRequest(
      credentials.idToken,
      await this.buildTwoFactor(credentials.twoFactor),
      await this.buildDeviceRequest(),
    );

    return this.startLogIn();
  }
}
